import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

class CartRewardGuard extends Component {
  #removing = false;

  connectedCallback() {
    super.connectedCallback();

    const announcement = sessionStorage.getItem('cart-reward-announcement');
    const status = this.querySelector('[data-reward-status]');
    if (announcement && status instanceof HTMLElement) {
      status.textContent = announcement;
      sessionStorage.removeItem('cart-reward-announcement');
    }

    this.removeIneligibleReward();
  }

  async removeIneligibleReward() {
    if (this.#removing || this.dataset.eligible === 'true' || !this.dataset.rewardKeys) return;

    const key = this.dataset.rewardKeys.split(',').filter(Boolean)[0];
    if (!key) return;

    this.#removing = true;
    await customElements.whenDefined('cart-items-component');

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: [{ id: key, quantity: 0 }],
        promise: deferredUpdatePromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_change_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({
            id: key,
            quantity: 0,
            sections: ['cart-drawer-section'],
          }),
        }),
      });

      if (!response.ok) throw new Error(`Reward removal failed with status ${response.status}`);

      const cart = await response.json();
      const html = cart.sections?.['cart-drawer-section'];
      if (!html) throw new Error('Reward removal response did not include the cart drawer section');

      if (this.dataset.removalMessage) {
        sessionStorage.setItem('cart-reward-announcement', this.dataset.removalMessage);
      }

      deferredUpdatePromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: cart.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'cart-reward-guard',
          didError: false,
        },
      });
    } catch (error) {
      deferredUpdatePromise.reject(error);

      if (error instanceof Error && error.name !== 'AbortError') {
        console.warn('[cart-rewards] Unable to remove an ineligible reward:', error);
      }
    } finally {
      this.#removing = false;
    }
  }
}

class CartRewardPicker extends Component {
  onVariantChange(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const picker = event.target.closest('[data-variant-picker]');
    if (!(picker instanceof HTMLElement)) return;

    const variantInput = picker.querySelector('[data-variant-input]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = event.target.value;
    }
  }
}

class CartRecommendationCarousel extends Component {
  #resizeObserver = new ResizeObserver(() => this.updateControls());

  connectedCallback() {
    super.connectedCallback();
    this.refs.scroller?.addEventListener('scroll', this.updateControls, { passive: true });
    this.#resizeObserver.observe(this);
    this.updateControls();
  }

  disconnectedCallback() {
    this.refs.scroller?.removeEventListener('scroll', this.updateControls);
    this.#resizeObserver.disconnect();
    super.disconnectedCallback();
  }

  previous = () => this.scroll(-1);

  next = () => this.scroll(1);

  scroll(direction) {
    const scroller = this.refs.scroller;
    if (!(scroller instanceof HTMLElement)) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scroller.scrollBy({
      left: direction * scroller.clientWidth * 0.82,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  updateControls = () => {
    const scroller = this.refs.scroller;
    const previous = this.refs.previous;
    const next = this.refs.next;
    if (!(scroller instanceof HTMLElement) || !(previous instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) {
      return;
    }

    previous.disabled = scroller.scrollLeft <= 1;
    next.disabled = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
  };

  onVariantChange(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const card = event.target.closest('[data-recommendation-product-id]');
    if (!(card instanceof HTMLElement)) return;

    const variantInput = card.querySelector('[data-variant-input]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = event.target.value;
    }

    const option = event.target.selectedOptions[0];
    const price = card.querySelector('[data-variant-price]');
    if (option?.dataset.price && price instanceof HTMLElement) {
      price.textContent = option.dataset.price;
    }
  }
}

class CartShippingInfo extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.clearTriggerPointerInteraction();

    this.trigger = this.querySelector('[data-shipping-info-trigger]');
    this.panel = this.querySelector('[data-shipping-info-panel]');
    if (!this.trigger || !this.panel) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.trigger.addEventListener('click', () => this.onTriggerClick(), { signal });
    this.trigger.addEventListener('pointerdown', (event) => this.onTriggerPointerDown(event), {
      signal,
    });
    this.addEventListener('pointerenter', (event) => this.onPointerEnter(event), { signal });
    this.addEventListener('pointerleave', (event) => this.onPointerLeave(event), { signal });
    this.addEventListener('focusin', () => this.onFocusIn(), { signal });
    this.addEventListener('focusout', (event) => this.onFocusOut(event), { signal });
    document.addEventListener('pointerdown', (event) => this.onDocumentPointerDown(event), {
      signal,
      capture: true,
    });
    document.addEventListener('pointerup', () => this.onDocumentPointerUp(), {
      signal,
      capture: true,
    });
    document.addEventListener('pointercancel', () => this.onDocumentPointerCancel(), {
      signal,
      capture: true,
    });
    document.addEventListener('keydown', (event) => this.onKeyDown(event), { signal });

    this.close();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.clearTriggerPointerInteraction();
    this.isFocusRestore = false;
  }

  get supportsHover() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  open() {
    this.setOpen(true);
  }

  close({ restoreFocus = false } = {}) {
    this.setOpen(false);
    if (!restoreFocus) return;

    this.isFocusRestore = true;
    this.trigger?.focus();
    this.isFocusRestore = false;
  }

  toggle() {
    this.setOpen(!this.hasAttribute('data-open'));
  }

  setOpen(isOpen) {
    this.toggleAttribute('data-open', isOpen);
    this.trigger.setAttribute('aria-expanded', String(isOpen));
    this.panel.setAttribute('aria-hidden', String(!isOpen));
  }

  onPointerEnter(event) {
    if (event.pointerType === 'mouse' && this.supportsHover) this.open();
  }

  onTriggerPointerDown(event) {
    this.clearTriggerPointerInteraction();
    this.isTriggerPointerInteraction = true;
    this.triggerPointerType = event.pointerType;
  }

  onDocumentPointerUp() {
    if (!this.isTriggerPointerInteraction) return;

    this.clearTriggerPointerInteractionTimer();
    this.triggerPointerInteractionTimer = window.setTimeout(
      () => this.clearTriggerPointerInteraction(),
      800
    );
  }

  onDocumentPointerCancel() {
    if (!this.isTriggerPointerInteraction) return;
    this.clearTriggerPointerInteraction();
  }

  onTriggerClick() {
    if (this.triggerPointerType === 'mouse' && this.supportsHover) this.open();
    else this.toggle();
    this.clearTriggerPointerInteraction();
  }

  clearTriggerPointerInteraction() {
    this.clearTriggerPointerInteractionTimer();
    this.isTriggerPointerInteraction = false;
    this.triggerPointerType = undefined;
  }

  clearTriggerPointerInteractionTimer() {
    if (this.triggerPointerInteractionTimer === undefined) return;

    window.clearTimeout(this.triggerPointerInteractionTimer);
    this.triggerPointerInteractionTimer = undefined;
  }

  onFocusIn() {
    if (this.isTriggerPointerInteraction || this.isFocusRestore) return;
    this.open();
  }

  onPointerLeave(event) {
    if (
      event.pointerType === 'mouse' &&
      this.supportsHover &&
      !this.contains(document.activeElement)
    ) {
      this.close();
    }
  }

  onFocusOut(event) {
    if (!this.contains(event.relatedTarget)) this.close();
  }

  onDocumentPointerDown(event) {
    if (!this.contains(event.target)) this.close();
  }

  onKeyDown(event) {
    if (event.key !== 'Escape' || !this.hasAttribute('data-open')) return;
    event.preventDefault();
    this.close({ restoreFocus: true });
  }
}

if (!customElements.get('cart-reward-guard')) {
  customElements.define('cart-reward-guard', CartRewardGuard);
}

if (!customElements.get('cart-reward-picker')) {
  customElements.define('cart-reward-picker', CartRewardPicker);
}

if (!customElements.get('cart-recommendation-carousel')) {
  customElements.define('cart-recommendation-carousel', CartRecommendationCarousel);
}

if (!customElements.get('cart-shipping-info')) {
  customElements.define('cart-shipping-info', CartShippingInfo);
}
